import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, RefreshCw } from 'lucide-react';
import { useChat } from '@/hooks';
import { ASSISTANTS } from '@/config/assistants.config';
import { assistantsService, convertApiAssistant } from '@/api/services/assistants.service';
import { Assistant } from '@/types';
import { renderAssistantIcon } from '@/utils/RenderAssistantIcon';
import { gtmService } from '@/services/retargeting/gtm.service';
import { fbPixelService } from '@/services/retargeting/fb.pixel.service';

export const AssistantSelector: React.FC = () => {
    const [showMenu, setShowMenu] = useState(false);
    const [assistants, setAssistants] = useState<Record<string, Assistant>>(ASSISTANTS);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const { selectedAssistant, setSelectedAssistant, startNewChat } = useChat();

    useEffect(() => {
        fetchAssistants();
    }, []);

    const fetchAssistants = async () => {
        setLoading(true);
        setError(null);

        try {
            const apiAssistants = await assistantsService.getPublishedAssistants();
            console.log('API Response:', apiAssistants);

            if (apiAssistants.length > 0) {
                const convertedAssistants: Record<string, Assistant> = {};
                Object.assign(convertedAssistants, ASSISTANTS);

                apiAssistants.forEach(apiAssistant => {
                    const assistant = convertApiAssistant(apiAssistant);
                    console.log('Converted assistant:', assistant);
                    convertedAssistants[assistant.id] = assistant;
                });

                console.log('Final assistants:', convertedAssistants);
                setAssistants(convertedAssistants);
            }
        } catch (error) {
            console.error('Failed to fetch assistants:', error);
            setError('Failed to load AI assistants');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setShowMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleAssistantSelection = (assistantId: string) => {
        const selectedAssistantData = assistants[assistantId];

        if (selectedAssistantData) {
            // Track with Google Ads using GTM service
            gtmService.trackAssistantSelected(
                selectedAssistantData.name,
                selectedAssistantData.category
            );

            // Track with Facebook Pixel service
            fbPixelService.trackAssistantSelection(
                selectedAssistantData.name,
                selectedAssistantData.category
            );
        }

        setSelectedAssistant(assistantId);
        setShowMenu(false);
        startNewChat();
    };

    return (
        <div ref={menuRef} className="relative">
            <button
                onClick={() => setShowMenu(!showMenu)}
                className="flex items-center gap-1 sm:gap-2 group"
            >
                <span className={`text-xs sm:text-sm font-medium transition-colors ${
                    showMenu ? 'text-black' : 'text-black group-hover:text-black'
                }`}>
                    Choose Assistant
                </span>
                <ChevronDown className={`size-3 sm:size-4 transition-all ${
                    showMenu ? 'rotate-180 text-black' : 'text-black group-hover:text-black'
                }`} />
            </button>

            {showMenu && (
                <div className="absolute right-0 mt-2 w-56 sm:w-64 md:w-72 bg-[#1a1a1a] rounded-lg shadow-xl border border-gray-700 z-50">
                    <div className="p-2 sm:p-3 border-b border-gray-700 flex items-center justify-between">
                        <h3 className="text-xs sm:text-sm font-semibold text-gray-200">Choose Assistant</h3>
                        <button
                            onClick={fetchAssistants}
                            className={`rounded-full transition-all duration-300 ease-in-out transform hover:scale-105 p-1 ${
                                loading
                                    ? 'bg-gray-500 cursor-not-allowed text-gray-300'
                                    : 'bg-cyan-400 hover:bg-cyan-500 hover:shadow-lg text-black'
                            }`}
                            disabled={loading}
                        >
                            <RefreshCw className={`size-3 sm:size-4 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>

                    {error && (
                        <div className="p-2 bg-red-900/20 text-red-400 text-xs border-b border-gray-700">
                            {error}
                        </div>
                    )}

                    <div className="p-2 max-h-96 overflow-y-auto">
                        {Object.values(assistants).map(assistant => {
                            const isSelected = selectedAssistant === assistant.id;
                            return (
                                <button
                                    key={assistant.id}
                                    onClick={() => handleAssistantSelection(assistant.id)}
                                    className="w-full p-2 sm:p-3 rounded-lg transition-all text-left hover:bg-cyan-400/20 hover:shadow-md transform hover:scale-[1.02] duration-200 ease-in-out"
                                >
                                    <div className="flex items-center gap-2 sm:gap-3">
                                        <div className="text-white flex-shrink-0">
                                            {renderAssistantIcon({
                                                assistant,
                                                size: 'md',
                                                className: 'size-4 sm:size-5'
                                            })}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium transition-colors text-white text-xs sm:text-sm truncate">{assistant.name}</div>
                                            <div className="text-xs text-gray-300 line-clamp-2">
                                                {assistant.description}
                                            </div>
                                            {assistant.category && (
                                                <div className="text-xs mt-1 text-gray-400">
                                                    {assistant.category}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}

                        {Object.keys(assistants).length === 0 && (
                            <div className="text-center py-4 text-gray-200 text-xs sm:text-sm">
                                No assistants available
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};